/* The trasseapp.com chrome: the design's sticky header (with a working phone
   menu and the current page underlined) and its dark footer. Every page of
   the marketing site renders inside this. */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './site.css';
import { SitePage, pageOf, useSiteGo } from './kit';

const MENU: [SitePage, string][] = [
  ['SiteHow', 'How it works'], ['SiteSchools', 'Schools'], ['SiteParents', 'Parents'], ['SiteDrivers', 'Drivers'],
  ['SiteZana', 'Zana'], ['SitePricing', 'Pricing'], ['SiteFamilies', 'Trasse Families'], ['SiteFAQ', 'Questions'], ['SiteSignIn', 'Sign in'],
];

const SiteLayout: React.FC<{ children: React.ReactNode; families?: boolean; bare?: boolean }> = ({ children, families, bare }) => {
  const go = useSiteGo();
  const loc = useLocation();
  const cur = pageOf(loc.pathname);
  const [menu, setMenu] = useState(false);
  const navCls = (k: SitePage) => 'navl' + (cur === k ? ' on' : '');
  // A link with an anchor lands on it (Pricing's "Talk to us" → /contact#groups); any other page starts at the top.
  // Pages load lazily: the page starts at the top at once, then the anchor is
  // looked for up to ten seconds (slow mobile data), and the page with the
  // anchor scrolls to it itself once it has drawn (Contact.tsx) in any case.
  useEffect(() => {
    setMenu(false);
    window.scrollTo(0, 0);
    if (!loc.hash) return undefined;
    let tries = 0;
    let t: number | undefined;
    const find = () => {
      const el = document.getElementById(loc.hash.slice(1));
      if (el) el.scrollIntoView({ block: 'start' });
      else if ((tries += 1) < 200) t = window.setTimeout(find, 50);
    };
    find();
    return () => window.clearTimeout(t);
  }, [loc.pathname, loc.hash]);
  useEffect(() => {
    if (!menu) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [menu]);
  if (bare) return <div className="site">{children}</div>;
  return (
    <div className="site">
    <div className={families ? 'fam' : undefined} style={{ display: 'contents' }}>
      <header style={{ position: 'sticky', top: '0', zIndex: '20', background: 'rgba(246,245,241,.88)', backdropFilter: 'saturate(1.4) blur(14px)', WebkitBackdropFilter: 'saturate(1.4) blur(14px)', borderBottom: '1px solid var(--line)' }}>
        <div className="wrap" style={{ height: '68px', display: 'flex', alignItems: 'center', gap: '40px' }}>
          <a {...go('SiteHome')} style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', color: 'var(--ink)' }} aria-label="Trasse home">
            <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
              <rect width="28" height="28" rx="7" fill="var(--ink)" />
              <path d="M10 7.5v10.2c0 2.2 1.2 3.3 3.3 3.3H17M7.5 11.5H17" stroke="var(--paper)" strokeWidth="2.6" fill="none" strokeLinecap="round" />
              <circle cx="20.5" cy="21" r="2.2" fill="#0FB8CE" />
            </svg>
            <span style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: '700', fontSize: '22px', letterSpacing: '-.01em' }}>
              trasse
            </span>
          </a>
          <nav className="desk" style={{ display: 'flex', gap: '28px', alignItems: 'center' }} aria-label="Main">
            <a className={navCls('SiteHow')} {...go('SiteHow')} aria-current={cur === 'SiteHow' ? 'page' : undefined}>
              How it works
            </a>
            <a className={navCls('SiteSchools')} {...go('SiteSchools')} aria-current={cur === 'SiteSchools' ? 'page' : undefined}>
              Schools
            </a>
            <a className={navCls('SiteParents')} {...go('SiteParents')} aria-current={cur === 'SiteParents' ? 'page' : undefined}>
              Parents
            </a>
            <a className={navCls('SiteDrivers')} {...go('SiteDrivers')} aria-current={cur === 'SiteDrivers' ? 'page' : undefined}>
              Drivers
            </a>
            <a className={navCls('SiteZana')} {...go('SiteZana')} aria-current={cur === 'SiteZana' ? 'page' : undefined}>
              Zana
            </a>
            <a className={navCls('SitePricing')} {...go('SitePricing')} aria-current={cur === 'SitePricing' ? 'page' : undefined}>
              Pricing
            </a>
          </nav>
          <div style={{ flex: '1' }} />
          <a className={navCls('SiteSignIn') + ' desk'} {...go('SiteSignIn')}>
            Sign in
          </a>
          <a className="btn btn-p desk" {...go('SiteBook')} style={{ height: '42px', fontSize: '15px', padding: '0 16px' }}>
            Book a free pilot
          </a>
          <a className="mob" {...go('SiteBook')} style={{ whiteSpace: 'nowrap', height: '40px', padding: '0 14px', borderRadius: '8px', background: 'var(--teal)', color: 'var(--on-teal)', fontWeight: '600', fontSize: '15px', alignItems: 'center' }}>
            Free pilot
          </a>
          <button type="button" className="mob" onClick={() => setMenu((o) => !o)} aria-label={menu ? 'Close menu' : 'Menu'} aria-expanded={menu} style={{ width: '40px', height: '40px', borderRadius: '8px', boxShadow: 'inset 0 0 0 1px var(--line2)', alignItems: 'center', justifyContent: 'center' , border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--ink)' }}>{menu ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg> : (<>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 7h16" />
              <path d="M4 12h16" />
              <path d="M4 17h16" />
            </svg>
          </>)}</button>
        </div>
      </header>
    {menu && (
      <nav className="menu mob" aria-label="Menu">
        {MENU.map(([k, label]) => <a key={k} className="mi" {...go(k)} aria-current={cur === k ? 'page' : undefined}>{label}</a>)}
        <a className="btn btn-p" {...go('SiteBook')} style={{ marginTop: 20, justifyContent: 'center' }}>Book a free pilot</a>
      </nav>
    )}
    <main>{children}</main>
      <footer style={{ background: 'var(--ink)', color: '#C9CED2' }}>
        <div className="wrap sp" style={{ paddingTop: '72px', paddingBottom: '40px' }}>
          <div className="grid" style={{ rowGap: '40px' }}>
            <div style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <span style={{ '--paper': 'var(--ink)' } as React.CSSProperties}>
                <a {...go('SiteHome')} style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', color: '#FFFFFF' }} aria-label="Trasse home">
                  <svg width="30" height="30" viewBox="0 0 28 28" aria-hidden="true">
                    <rect width="28" height="28" rx="7" fill="#FFFFFF" />
                    <path d="M10 7.5v10.2c0 2.2 1.2 3.3 3.3 3.3H17M7.5 11.5H17" stroke="var(--paper)" strokeWidth="2.6" fill="none" strokeLinecap="round" />
                    <circle cx="20.5" cy="21" r="2.2" fill="#0FB8CE" />
                  </svg>
                  <span style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: '700', fontSize: '24px', letterSpacing: '-.01em' }}>
                    trasse
                  </span>
                </a>
              </span>
              {' '}
              <p className="sm" style={{ margin: '0', maxWidth: '300px', color: '#A6ADB3' }}>
                School transport, run properly. Built in Nairobi by Pearlabs Limited.
              </p>
              {' '}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <a className="btn btn-p" {...go('SiteBook')} style={{ height: '44px', fontSize: '15px' }}>
                  Book a free pilot
                </a>
                <a className="btn" {...go('SiteContact')} style={{ height: '44px', fontSize: '15px', color: '#fff', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.2)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 21l1.6-4.8A8.5 8.5 0 1 1 8 20Z" />
                    <path d="M9 9.5c.3 1.6 1.9 3.7 3.8 4.6l1.2-1.1 2 .8c0 1-.8 1.9-1.9 1.9-3.2-.2-6.3-3.3-6.5-6.5 0-1 .9-1.9 1.9-1.9l.8 2Z" />
                  </svg>
                  {' '}WhatsApp us
                </a>
              </div>
            </div>
            {' '}
            <div style={{ gridColumn: '6 / span 2' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span className="mono" style={{ color: '#8C9196' }}>
                  Product
                </span>
                <a {...go('SiteHow')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  How it works
                </a>
                <a {...go('SiteZana')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Zana
                </a>
                <a {...go('SitePricing')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Pricing
                </a>
                <a {...go('SiteFamilies')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Trasse Families
                </a>
                <a {...go('SiteSignIn')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Sign in
                </a>
              </div>
            </div>
            {' '}
            <div style={{ gridColumn: 'span 2' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span className="mono" style={{ color: '#8C9196' }}>
                  Who it’s for
                </span>
                <a {...go('SiteSchools')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Schools
                </a>
                <a {...go('SiteParents')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Parents
                </a>
                <a {...go('SiteDrivers')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Drivers
                </a>
                <a {...go('SiteFAQ')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Questions
                </a>
              </div>
            </div>
            {' '}
            <div style={{ gridColumn: 'span 2' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span className="mono" style={{ color: '#8C9196' }}>
                  Company
                </span>
                <a {...go('SiteAbout')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  About
                </a>
                <a {...go('SiteSafety')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Safety
                  &
                  data
                </a>
                <a {...go('SiteContact')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Contact
                </a>
                <a {...go('SitePrivacy')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Privacy
                </a>
                <a {...go('SiteTerms')} style={{ fontSize: '15px', color: '#D9DDE0' }}>
                  Terms
                </a>
              </div>
            </div>
          </div>
          {' '}
          <div style={{ height: '1px', background: 'rgba(255,255,255,.1)', margin: '56px 0 20px' }} />
          {' '}
          <div className="stack" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13px', color: '#8C9196' }}>
            <span>
              © 2026 Pearlabs Limited · Nairobi, Kenya
            </span>
            <span>
              Map data © OpenStreetMap contributors
            </span>
          </div>
        </div>
      </footer>
    </div>
    </div>
  );
};

export default SiteLayout;
